
import * as React from "react";
import { motion, useMotionValue, useMotionTemplate } from "framer-motion";
import { cn } from "@/lib/utils";
import heroIpad from "@/assets/quickatoo-hero-ipad.png";

interface HeroSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  intro?: string[];
  punchline?: string;
  ctaText?: string;
  ctaHref?: string;
  children?: React.ReactNode;
}

const HeroSection = React.forwardRef<HTMLDivElement, HeroSectionProps>(({
  className,
  title = "The quickest way to quote, plan and manage any painting job.",
  intro = [
    "Level up your painting business.",
    "Quickatoo gives you total control to deliver professional, accurate, and detailed quotes in minutes - not hours."
  ],
  punchline = "Win more jobs & get more done.",
  ctaText = "Unlock Your Success",
  ctaHref = "https://app.quickatoo.com/auth/login?view=sign_up",
  children,
  ...props
}, ref) => {
  // Cursor position drives the radial mask that reveals the brighter grid layer.
  const mouseX = useMotionValue(-600);
  const mouseY = useMotionValue(-600);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const { left, top } = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - left);
    mouseY.set(e.clientY - top);
  };

  const maskImage = useMotionTemplate`radial-gradient(340px circle at ${mouseX}px ${mouseY}px, black, transparent)`;

  return (
    <div ref={ref} className={cn("relative w-full", className)} {...props}>
      <section
        onMouseMove={handleMouseMove}
        className="relative w-full overflow-hidden bg-quickatoo-purple"
      >
        {/* Animated grid background (base + cursor-revealed layers) */}
        <div className="hero-grid absolute inset-0 z-0 opacity-[0.10]" />
        <motion.div
          className="hero-grid absolute inset-0 z-0 opacity-60"
          style={{ maskImage, WebkitMaskImage: maskImage }}
        />

        {/* Brand glow blobs */}
        <div className="pointer-events-none absolute inset-0 z-0">
          <div className="absolute right-[-10%] top-[-25%] h-[55%] w-[45%] rounded-full bg-quickatoo-yellow/25 blur-[130px]" />
          <div className="absolute left-[-8%] bottom-[-30%] h-[55%] w-[40%] rounded-full bg-[#B492FF]/45 blur-[130px]" />
          <div className="absolute right-[22%] top-[-10%] h-[25%] w-[20%] rounded-full bg-white/10 blur-[100px]" />
        </div>

        <div className="relative z-10 flex w-full items-stretch">
          <div className="min-w-0 flex-1 px-4 pb-12 pt-10 text-white sm:px-8 lg:px-12 lg:pb-20 lg:pt-16 xl:pt-20">
            <h1 className="max-w-3xl font-display text-4xl leading-[1.2] md:text-5xl xl:text-[56px] 2xl:text-[64px]">
              {title}
            </h1>
            <div className="mt-6 max-w-xl space-y-4 text-lg md:text-xl">
              {intro.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
            <p className="mt-10 font-display text-2xl md:text-[26px]">
              {punchline}
            </p>
            <a
              href={ctaHref}
              className="mt-6 inline-flex h-[52px] items-center justify-center rounded-3xl bg-quickatoo-yellow px-6 text-lg font-semibold text-quickatoo-charcoal transition-transform hover:scale-105"
            >
              {ctaText}
            </a>
          </div>
          <div className="relative hidden w-[42vw] max-w-[820px] shrink-0 lg:block">
            <img
              src={heroIpad}
              alt="Quickatoo app on a tablet"
              className="absolute bottom-0 right-0 h-full w-full object-contain object-[right_bottom]"
            />
          </div>
        </div>
      </section>
      {children}
    </div>
  );
});

HeroSection.displayName = "HeroSection";

export { HeroSection };
